import SignClient from '@walletconnect/sign-client';
import type { SessionTypes } from '@walletconnect/types';

let wcClientPromise: Promise<SignClient> | null = null;
let subscribed = false;

export type WalletConnectSessionProposal = {
  id: number;
  proposerName: string;
  proposerUrl: string;
  proposerDescription: string;
  proposerIcons: string[];
  requiredNamespaces: Record<string, { chains?: string[]; methods: string[]; events: string[] }>;
  optionalNamespaces: Record<string, { chains?: string[]; methods: string[]; events: string[] }>;
  raw: unknown;
};

export type WalletConnectSessionRequest = {
  topic: string;
  id: number;
  chainId: string;
  method: string;
  params: unknown;
  raw: unknown;
};

const proposalListeners = new Set<(p: WalletConnectSessionProposal) => void>();
const requestListeners = new Set<(r: WalletConnectSessionRequest) => void>();

function isWalletConnectUri(uri: string): boolean {
  return /^wc:/i.test(uri.trim());
}

async function getWalletConnectClient(): Promise<SignClient> {
  if (!wcClientPromise) {
    const projectId = (import.meta.env.VITE_WALLETCONNECT_PROJECT_ID ?? '').trim();
    if (!projectId) {
      throw new Error('WALLETCONNECT_PROJECT_ID_MISSING');
    }
    wcClientPromise = SignClient.init({
      projectId,
      metadata: {
        name: 'Cointrader Wallet',
        description: 'Cointrader wallet connection',
        url: window.location.origin,
        icons: [`${window.location.origin}/favicon.ico`],
      },
    });
  }
  const client = await wcClientPromise;
  if (!subscribed) {
    subscribed = true;
    client.on('session_proposal', (evt) => {
      const p: WalletConnectSessionProposal = {
        id: Number(evt.id),
        proposerName: evt.params.proposer.metadata.name ?? '',
        proposerUrl: evt.params.proposer.metadata.url ?? '',
        proposerDescription: evt.params.proposer.metadata.description ?? '',
        proposerIcons: evt.params.proposer.metadata.icons ?? [],
        requiredNamespaces: evt.params.requiredNamespaces ?? {},
        optionalNamespaces: evt.params.optionalNamespaces ?? {},
        raw: evt,
      };
      proposalListeners.forEach((fn) => fn(p));
    });
    client.on('session_request', (evt) => {
      const r: WalletConnectSessionRequest = {
        topic: evt.topic,
        id: Number(evt.id),
        chainId: evt.params.chainId,
        method: evt.params.request.method,
        params: evt.params.request.params,
        raw: evt,
      };
      requestListeners.forEach((fn) => fn(r));
    });
  }
  return client;
}

/** Pair the scanned wc: URI with WalletConnect relay. */
export async function pairWalletConnectUri(uriRaw: string): Promise<void> {
  const uri = uriRaw.trim();
  if (!isWalletConnectUri(uri)) {
    throw new Error('WALLETCONNECT_URI_INVALID');
  }
  const client = await getWalletConnectClient();
  await client.pair({ uri });
}

export async function ensureWalletConnectReady(): Promise<void> {
  await getWalletConnectClient();
}

export function onWalletConnectSessionProposal(cb: (p: WalletConnectSessionProposal) => void): () => void {
  proposalListeners.add(cb);
  return () => proposalListeners.delete(cb);
}

export function onWalletConnectSessionRequest(cb: (r: WalletConnectSessionRequest) => void): () => void {
  requestListeners.add(cb);
  return () => requestListeners.delete(cb);
}

export async function approveWalletConnectSession(
  proposalId: number,
  proposalRaw: unknown,
  eip155Address: string,
  chains: string[],
): Promise<void> {
  const client = await getWalletConnectClient();
  const methods = new Set<string>();
  const events = new Set<string>();
  const raw = proposalRaw as {
    params?: {
      requiredNamespaces?: Record<string, { methods?: string[]; events?: string[] }>;
      optionalNamespaces?: Record<string, { methods?: string[]; events?: string[] }>;
    };
  };
  const req = raw.params?.requiredNamespaces?.eip155;
  const opt = raw.params?.optionalNamespaces?.eip155;
  (req?.methods ?? []).forEach((m) => methods.add(m));
  (opt?.methods ?? []).forEach((m) => methods.add(m));
  (req?.events ?? []).forEach((m) => events.add(m));
  (opt?.events ?? []).forEach((m) => events.add(m));

  const namespace: SessionTypes.Namespace = {
    accounts: chains.map((ch) => `${ch}:${eip155Address}`),
    methods: Array.from(methods),
    events: Array.from(events),
  };
  await client.approve({
    id: proposalId,
    namespaces: { eip155: namespace },
  });
}

export async function rejectWalletConnectSession(proposalId: number, message = 'User rejected'): Promise<void> {
  const client = await getWalletConnectClient();
  await client.reject({
    id: proposalId,
    reason: { code: 5000, message },
  });
}

export async function respondWalletConnectRequestResult(topic: string, id: number, result: unknown): Promise<void> {
  const client = await getWalletConnectClient();
  await client.respond({
    topic,
    response: { id, jsonrpc: '2.0', result },
  });
}

export async function respondWalletConnectRequestError(topic: string, id: number, code: number, message: string): Promise<void> {
  const client = await getWalletConnectClient();
  await client.respond({
    topic,
    response: { id, jsonrpc: '2.0', error: { code, message } },
  });
}

